import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import Typography from '@mui/material/Typography';
import {
  Fragment,
  useEffect,
  useRef,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';

import { testIdPart } from '@/lib/testIds';

import { Button } from './Button';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { LiveRegion } from './LiveRegion';

export type SortDirection = 'asc' | 'desc';

export interface SortState<SortKey extends string = string> {
  /** The column's `sortKey`, not its `key`. */
  column: SortKey;
  direction: SortDirection;
}

/**
 * `SortKey` is the set of names the server accepts. Leaving it as `string`
 * suits a list that cannot be ordered; a page that can order passes its own
 * union, and then a column cannot claim a sort key the server has never heard
 * of.
 */
export interface Column<Row, SortKey extends string = string> {
  /** Identifies the column; also the React key. */
  key: string;
  header: string;
  /** The header is read to screen readers but not drawn, as for a drag handle's column. */
  headerHidden?: boolean;
  render: (row: Row) => ReactNode;
  align?: 'left' | 'right';
  /**
   * The name the server knows this column by. A column without one shows no
   * sort control, because there is nothing to ask the server for.
   */
  sortKey?: SortKey;
}

/** What a wrapping caller may add to a row's `<tr>`. */
export interface RowHooks {
  ref?: (element: HTMLTableRowElement | null) => void;
  style?: CSSProperties;
}

interface DataTableProps<Row, SortKey extends string = string> {
  columns: Column<Row, SortKey>[];
  rows: Row[];
  getRowKey: (row: Row) => string;
  caption: string;
  /**
   * True before the first rows arrive. The table is drawn with its caption and
   * real headers and a page of placeholder rows, so it is the size it will be.
   */
  loading?: boolean;
  /** How many placeholder rows to draw while loading. Defaults to a page. */
  loadingRows?: number;
  error?: string | null;
  onRetry?: () => void;
  /** Shown when there is genuinely nothing to list. */
  emptyMessage?: string;
  /** Shown when filters have excluded everything. A different situation. */
  noMatchMessage?: string;
  /** True when the caller has narrowed the list. */
  filtered?: boolean;
  /**
   * True while a fresh page is on its way and the previous one is still shown.
   *
   * Distinct from `loading`, which replaces the whole table. Replacing it on a
   * page change would unmount the Next button the person just pressed and drop
   * their focus onto nothing, so this dims the rows instead and leaves every
   * control where it was.
   */
  refreshing?: boolean;

  /** Which column orders the rows, and which way. Null means the caller's default. */
  sort?: SortState<SortKey> | null;
  /** Called with a column's `sortKey` when its header is chosen. */
  onSortChange?: (column: SortKey) => void;

  /** The page being shown, counting from one. */
  page?: number;
  pageSize?: number;
  /** How many rows matched in total, across every page. */
  total?: number;
  onPageChange?: (page: number) => void;

  /**
   * Called when a body row is clicked anywhere, for a list whose rows open
   * something.
   *
   * For the pointer only. A row is not made focusable or given a role - a
   * `<tr>` made a button stops being a row to a screen reader - so the caller
   * puts one focusable control in the row that does the same thing.
   */
  onRowClick?: (row: Row) => void;

  /**
   * Wraps each row, for a caller that needs its own hold on the `<tr>` - a
   * sortable list puts its ref and style on it. `render` draws the row as
   * usual, with whatever is passed added to the `<tr>`.
   */
  wrapRow?: (row: Row, render: (hooks?: RowHooks) => ReactElement) => ReactNode;

  /**
   * Names the table for the testing team's tests. Each row gets
   * `<testId>-row-<row key>` and each cell `<testId>-cell-<column key>`; the
   * headers, sort controls, states and paging have their own. See
   * docs/test-ids.md.
   */
  testId: string;
}

/**
 * The table every list page uses.
 *
 * Built on the plain MUI table rather than the X Data Grid: the grid earns its
 * weight once rows are virtualised or columns are resizable, neither of which
 * any story asks for, and it would bring its own theming and accessibility
 * surface to re-establish. Keeping the decision behind this wrapper means
 * moving to the grid later changes one file.
 *
 * It orders and pages nothing itself. The server does both, so the table
 * renders the rows it is given and reports which header was chosen and which
 * page was asked for. Sorting here as well would quietly reorder one page of a
 * list ordered as a whole, which looks right and is wrong.
 */
export function DataTable<Row, SortKey extends string = string>({
  columns,
  rows,
  getRowKey,
  caption,
  loading = false,
  loadingRows,
  error = null,
  onRetry,
  emptyMessage = 'Nothing to show yet.',
  noMatchMessage = 'Nothing matched your search.',
  filtered = false,
  refreshing = false,
  sort = null,
  onSortChange,
  page,
  pageSize,
  total,
  onPageChange,
  onRowClick,
  wrapRow,
  testId,
}: DataTableProps<Row, SortKey>) {
  /*
   * A new page starts at its first row. The rows scroll inside the table, so
   * after scrolling to the last row and pressing Next the new page used to
   * open at its bottom. The window is moved too, but only if the top of the
   * table is out of sight - under the fixed top bar or above the screen.
   * Only a change counts: arriving on a list must not move the window.
   */
  const containerRef = useRef<HTMLDivElement>(null);
  const shownPage = useRef(page);
  useEffect(() => {
    if (shownPage.current === page) return;
    shownPage.current = page;
    const container = containerRef.current;
    if (!container) return;
    container.scrollTop = 0;
    container.scrollIntoView({ block: 'nearest' });
  }, [page]);

  // The shared states, so every list fails and empties the same way, under
  // the IDs this table has always given them.
  if (error) {
    return <ErrorState title={error} onRetry={onRetry} testId={testId} />;
  }

  if (!loading && rows.length === 0) {
    return (
      <EmptyState title={filtered ? noMatchMessage : emptyMessage} testId={testId} />
    );
  }

  // Paging is offered only when the caller supplies all of its parts and there
  // is more than one page. A control that cannot go anywhere is noise.
  const paged =
    page !== undefined &&
    pageSize !== undefined &&
    total !== undefined &&
    onPageChange !== undefined;
  const lastPage = paged ? Math.max(1, Math.ceil(total / pageSize)) : 1;
  const showPaging = paged && lastPage > 1;
  // Placeholder rows while loading, as many as a page will hold.
  const placeholders = Array.from({ length: loadingRows ?? pageSize ?? 5 }, (_, i) => i);

  return (
    <>
      <TableContainer
        ref={containerRef}
        component={Paper}
        variant="outlined"
        aria-busy={loading || refreshing || undefined}
        /*
         * A region with a name and a tab stop, so the table can be scrolled
         * sideways by keyboard as well as by mouse. Without the tab stop it
         * never receives focus and the arrow keys never reach it, which on a
         * phone left three of the five columns simply unreachable.
         */
        role="region"
        aria-label={caption}
        tabIndex={0}
        data-testid={testId}
        sx={{
          /*
           * Tall lists get their own vertical scroll rather than growing the
           * page. The container used to be as tall as its contents - nearly
           * 2,000 pixels on a phone - which put its own horizontal scrollbar
           * about 1,160 pixels below the bottom of the screen. It could be
           * scrolled, but nobody could ever see how.
           */
          maxHeight: { xs: '65vh', md: '70vh' },
          // Dimmed and inert while the next page is fetched, so it is obvious
          // something is happening and stale rows cannot be clicked.
          opacity: refreshing ? 0.5 : 1,
          pointerEvents: refreshing ? 'none' : undefined,
          transition: 'opacity 120ms',
          // The focus ring belongs on the outside, or the rows clip it.
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        {/* A caption names the table for a screen reader without taking space. */}
        <Table size="small" stickyHeader>
          <Box component="caption" sx={{ position: 'absolute', left: -9999 }}>
            {caption}
          </Box>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const sortKey = column.sortKey;
                const sortable = Boolean(sortKey && onSortChange);
                // Null unless this is the column currently ordering the rows,
                // which keeps the direction and the active flag in step.
                const direction =
                  sortable && sort && sort.column === sortKey ? sort.direction : null;

                return (
                  <TableCell
                    key={column.key}
                    align={column.align ?? 'left'}
                    // Sets aria-sort, which is how a screen reader is told
                    // which column orders the table and which way.
                    sortDirection={direction ?? false}
                    data-testid={`${testId}-header-${testIdPart(column.key)}`}
                  >
                    {sortable && sortKey ? (
                      <TableSortLabel
                        active={direction !== null}
                        direction={direction ?? 'asc'}
                        onClick={() => onSortChange?.(sortKey)}
                        data-testid={`${testId}-sort-${testIdPart(column.key)}-button`}
                      >
                        {column.header}
                      </TableSortLabel>
                    ) : column.headerHidden ? (
                      <Box component="span" sx={visuallyHidden}>
                        {column.header}
                      </Box>
                    ) : (
                      column.header
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Hidden from a screen reader, which hears "Loading…" once from
                the status below instead of a page of empty cells. */}
            {loading
              ? placeholders.map((index) => (
                  <TableRow
                    key={`loading-${String(index)}`}
                    aria-hidden="true"
                    data-testid={`${testId}-loading-row`}
                  >
                    {columns.map((column) => (
                      <TableCell key={column.key} align={column.align ?? 'left'}>
                        <Skeleton
                          width="60%"
                          sx={column.align === 'right' ? { ml: 'auto' } : undefined}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : null}
            {(loading ? [] : rows).map((row) => {
              const render = (hooks: RowHooks = {}) => (
                <TableRow
                  key={getRowKey(row)}
                  ref={hooks.ref}
                  style={hooks.style}
                  hover
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={onRowClick ? { cursor: 'pointer' } : undefined}
                  data-testid={`${testId}-row-${testIdPart(getRowKey(row))}`}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align ?? 'left'}
                      data-testid={`${testId}-cell-${testIdPart(column.key)}`}
                    >
                      {column.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
              return wrapRow ? (
                <Fragment key={getRowKey(row)}>{wrapRow(row, render)}</Fragment>
              ) : (
                render()
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {loading ? <LiveRegion message="Loading…" /> : null}

      {paged && !loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mt: 2,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid={`${testId}-page-info`}
          >
            {showPaging
              ? `Page ${page} of ${lastPage} · ${total} ${total === 1 ? 'match' : 'matches'}`
              : `${total} ${total === 1 ? 'match' : 'matches'}`}
          </Typography>

          {showPaging ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
                data-testid={`${testId}-previous-button`}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= lastPage}
                onClick={() => onPageChange(page + 1)}
                data-testid={`${testId}-next-button`}
              >
                Next
              </Button>
            </Box>
          ) : null}

          {/* The rows are replaced without focus moving, so a screen-reader
              user would otherwise find different content with no explanation. */}
          <LiveRegion
            message={
              refreshing
                ? 'Loading…'
                : showPaging
                  ? `Showing page ${page} of ${lastPage}.`
                  : ''
            }
          />
        </Box>
      ) : null}
    </>
  );
}
